import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Paperclip, Plus, ShoppingCart } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import Button from '../../components/ui/Button';
import sprayService from '../../services/spray';
import purchaseOrderService from '../../services/purchaseOrders';
import orchardService from '../../services/orchard';
import { usePurchaseOrderAccess } from '../../hooks/usePurchaseOrderAccess';
import { useOrgTimezone, formatDateInTz, formatLocalDateInTz } from '../../utils/timezone';
import { errorMessage } from '../../utils/apiResponse';
import { money, num } from '../../utils/nurseryMoney';
import type { PurchaseOrder, POStatus } from '../../types/purchaseOrders';
import type { Farm } from '../../types/orchard';

/**
 * /purchase-orders: what the organisation has ordered, for which farm, from
 * whom and by whom. Someone the organisation's admin has authorised raises an
 * order from here; an Accounts seat reads the same list, so a supplier's
 * invoice can be matched to the order it quotes. The route's
 * PurchaseOrdersGate lets both in.
 */

const STATUS_LABELS: Record<POStatus, string> = {
  draft: 'Draft',
  ordered: 'Ordered',
  partially_received: 'Partially received',
  received: 'Received',
  cancelled: 'Cancelled',
};

const STATUS_COLORS: Record<POStatus, string> = {
  draft: 'bg-neutral-100 text-neutral-700',
  ordered: 'bg-blue-100 text-blue-700',
  partially_received: 'bg-amber-100 text-amber-800',
  received: 'bg-green-100 text-green-700',
  cancelled: 'bg-red-100 text-red-700',
};

const PAGE_SIZE = 25;

function PurchaseOrderList() {
  const tz = useOrgTimezone();
  const navigate = useNavigate();
  const { canRaise } = usePurchaseOrderAccess();

  const [farms, setFarms] = useState<Farm[]>([]);
  const [farm, setFarm] = useState('');
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(1);
  const [orders, setOrders] = useState<PurchaseOrder[]>([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Archived farms too: an order raised for one is still an order.
    orchardService.getFarms(1, 200, sprayService.getTargetOrgId() ?? undefined, true)
      .then((res) => { if (res.success && res.data?.farms) setFarms(res.data.farms); })
      .catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await purchaseOrderService.list({
        status: status || undefined,
        farm_id: farm === 'none' ? 'none' : farm ? parseInt(farm) : undefined,
        page,
        limit: PAGE_SIZE,
      });
      if (res.success && res.data) {
        setOrders(res.data.pos);
        setTotal(res.data.total);
        setTotalPages(res.data.totalPages);
      } else {
        setError(errorMessage(res, 'Could not load the purchase orders'));
      }
    } catch (e) {
      setError(errorMessage(e, 'Could not load the purchase orders'));
    } finally {
      setLoading(false);
    }
  }, [status, farm, page]);

  useEffect(() => { load(); }, [load]);

  const filtered = Boolean(status || farm);

  return (
    <>
      <PageHeader
        title="Purchase Orders"
        subtitle="What has been ordered, for which farm, from whom and by whom."
        icon={<ShoppingCart className="h-6 w-6 text-white" />}
        actionButtons={canRaise ? (
          <Button variant="primary" size="sm" onClick={() => navigate('/purchase-orders/new')}>
            <Plus className="h-4 w-4" />
            New Purchase Order
          </Button>
        ) : undefined}
      />
      <div className="max-w-4xl mx-auto px-4 py-6 space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="sm:col-span-2">
            <label htmlFor="po-farm-filter" className="block text-sm font-medium text-neutral-700 mb-1">Farm</label>
            <select id="po-farm-filter" value={farm} onChange={(e) => { setFarm(e.target.value); setPage(1); }}
              className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm">
              <option value="">All farms</option>
              {farms.map((f) => <option key={f.id} value={f.id}>{f.code ? `${f.name} (${f.code})` : f.name}</option>)}
              <option value="none">No farm recorded</option>
            </select>
          </div>
          <div>
            <label htmlFor="po-status-filter" className="block text-sm font-medium text-neutral-700 mb-1">Show</label>
            <select id="po-status-filter" value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }}
              className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm">
              <option value="">All orders</option>
              {(Object.keys(STATUS_LABELS) as POStatus[]).map((s) => <option key={s} value={s}>{STATUS_LABELS[s]}</option>)}
            </select>
          </div>
        </div>

        {error && <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700 break-words">{error}</div>}

        {loading ? (
          <div className="h-40 bg-neutral-100 rounded-xl animate-pulse" />
        ) : orders.length === 0 ? (
          <div className="bg-white border border-neutral-200 rounded-xl p-8 text-center text-sm text-neutral-500">
            {filtered
              ? 'No purchase orders match. Try another farm, or show all orders.'
              : canRaise
                ? 'No purchase orders yet. Raise the first one with New Purchase Order.'
                : 'No purchase orders yet. The people your organisation’s admin has authorised raise them here.'}
          </div>
        ) : (
          <>
            <p className="text-xs text-neutral-500">{total} order{total !== 1 ? 's' : ''}</p>
            <ul className="space-y-3">
              {orders.map((po) => {
                const lines = num(po.line_item_count);
                const costed = num(po.costed_line_count);
                const raisedBy = [po.creator_first_name, po.creator_last_name].filter(Boolean).join(' ');
                return (
                  <li key={po.id}>
                    <Link to={`/purchase-orders/${po.id}`}
                      className="block bg-white border border-neutral-200 rounded-xl p-4 hover:border-primary-300">
                      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                        <span className="font-semibold text-neutral-900 break-words min-w-0">
                          {po.reference_number ?? `PO #${po.id}`}
                        </span>
                        <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold ${STATUS_COLORS[po.status]}`}>
                          {STATUS_LABELS[po.status]}
                        </span>
                        {num(po.document_count) > 0 && (
                          <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold bg-indigo-100 text-indigo-700">
                            <Paperclip className="h-3 w-3" />
                            {num(po.document_count) === 1 ? 'Receipt attached' : `${num(po.document_count)} receipts attached`}
                          </span>
                        )}
                        {po.paid_by_card && (
                          <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold bg-amber-100 text-amber-800">
                            Paid on card
                          </span>
                        )}
                        {/* Matched to the supplier's bill in Xero, or refused by Xero. */}
                        {po.xero_status === 'BILLED' && (
                          <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold bg-green-100 text-green-700">
                            Billed in Xero
                          </span>
                        )}
                        {po.xero_sync_status === 'failed' && (
                          <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold bg-red-100 text-red-700">
                            Not sent to Xero
                          </span>
                        )}
                      </div>
                      <p className="mt-1 text-sm text-neutral-800 break-words">
                        For {po.farm_name ?? 'no farm recorded'}
                      </p>
                      <p className="text-sm text-neutral-600 break-words">
                        {po.supplier_org_name ?? po.supplier_name ?? 'No supplier'}
                      </p>
                      {po.items_summary && (
                        <p className="mt-1 text-sm text-neutral-600 break-words line-clamp-2">{po.items_summary}</p>
                      )}
                      <div className="mt-1 text-xs text-neutral-500 flex flex-wrap gap-x-3 gap-y-0.5">
                        <span className="break-words min-w-0">
                          Raised {formatDateInTz(po.created_at, tz)}{raisedBy && ` by ${raisedBy}`}
                        </span>
                        {po.ordered_date && <span>Ordered {formatLocalDateInTz(po.ordered_date.slice(0, 10), tz)}</span>}
                        {po.expected_delivery_date && (
                          <span>Expected {formatLocalDateInTz(po.expected_delivery_date.slice(0, 10), tz)}</span>
                        )}
                      </div>
                      <div className="mt-2 text-sm text-neutral-600 break-words">
                        {costed === 0 ? (
                          <>No costs entered</>
                        ) : (
                          <>
                            Total <span className="font-semibold text-neutral-900">{money(po.total_cost)}</span>
                            {costed < lines && ` (${lines - costed} of ${lines} items with no cost entered)`}
                          </>
                        )}
                      </div>
                    </Link>
                  </li>
                );
              })}
            </ul>
          </>
        )}

        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-2">
            <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)}
              className="px-3 py-1 text-sm border border-neutral-300 rounded-lg disabled:opacity-40 hover:bg-neutral-50">
              Previous
            </button>
            <span className="text-sm text-neutral-500">{page} / {totalPages}</span>
            <button disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}
              className="px-3 py-1 text-sm border border-neutral-300 rounded-lg disabled:opacity-40 hover:bg-neutral-50">
              Next
            </button>
          </div>
        )}
      </div>
    </>
  );
}

export default PurchaseOrderList;
